import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputBase,
  Divider,
} from "@mui/material";
import {
  ArrowBack,
  Visibility,
  History,
  Image as ImageIcon,
  TrendingUp,
  TrendingDown,
  SwapHoriz,
  Payment,
  Undo,
  Search,
  Clear,
  AccountBalanceWallet,
  Tune,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  getMyTransactionHistory,
  getTransactionDetail,
  getEvidencePhotoUrl,
} from "api/wallet";
import AuthenticatedImage from "components/AuthenticatedImage";
import { formatWalletTxnDetail } from "utils/formatWalletTxnDetail";

const purposeConfig = {
  deposit: {
    label: "Deposit",
    color: "success",
    icon: <TrendingUp />,
    sign: "+",
  },
  refund: { label: "Refund", color: "success", icon: <Undo />, sign: "+" },
  withdraw: {
    label: "Withdraw",
    color: "error",
    icon: <TrendingDown />,
    sign: "-",
  },
  payment: { label: "Payment", color: "error", icon: <Payment />, sign: "-" },
  transfer: { label: "Transfer", color: "info", icon: <SwapHoriz />, sign: "" },
  exchange: { label: "Exchange", color: "info", icon: <SwapHoriz />, sign: "" },
  reversal: { label: "Reversal", color: "warning", icon: <Undo />, sign: "" },
  adjustment: {
    label: "Adjustment",
    color: "warning",
    icon: <AccountBalanceWallet />,
    sign: "",
  },
};

const currencySymbols = { USD: "$", THB: "฿", LAK: "₭" };
const currencyFlags = { USD: "us", THB: "th", LAK: "la" };

function CurrencyFlag({ currency }) {
  return (
    <img
      src={`https://flagcdn.com/16x12/${currencyFlags[currency] || "un"}.png`}
      alt={currency}
      style={{
        width: 16,
        height: 12,
        marginLeft: 4,
        verticalAlign: "middle",
        borderRadius: 1,
      }}
    />
  );
}

function formatCurrency(amount, currency) {
  return `${currencySymbols[currency] || ""}${Number(amount).toLocaleString()}`;
}

function formatDate(d) {
  return new Date(d).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getPurposeConfig(transaction) {
  const purpose =
    transaction.purpose ||
    (transaction.txn_type === "credit" ? "deposit" : "withdraw");
  return (
    purposeConfig[purpose] || {
      label: purpose,
      color: "default",
      icon: <AccountBalanceWallet />,
      sign: "",
    }
  );
}

function getAmountSign(transaction) {
  const cfg = getPurposeConfig(transaction);
  if (cfg.sign) return cfg.sign;
  return transaction.txn_type === "credit" ? "+" : "-";
}

const TransactionHistory = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState("ALL");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");

  // Detail dialog
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [imageDialog, setImageDialog] = useState(false);
  const [, setSelectedTxn] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchTransactions = async (p = page, filters = buildFilters()) => {
    setLoading(true);
    try {
      const res = await getMyTransactionHistory(
        p,
        pageSize,
        {},
        filters.search,
        {
          currency: filters.currency !== "ALL" ? filters.currency : undefined,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
          minAmount: filters.minAmount,
          maxAmount: filters.maxAmount,
        },
      );
      if (res.status === 200) {
        setRows(res.data);
        setTotalCount(res.total);
      }
    } catch (e) {
      setError(e.message || "Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  function buildFilters() {
    return {
      search,
      currency: currencyFilter,
      dateFrom,
      dateTo,
      minAmount,
      maxAmount,
    };
  }

  useEffect(() => {
    fetchTransactions(page);
  }, [page]);

  const handleApplyFilters = () => {
    setPage(1);
    fetchTransactions(1);
  };

  const handleClearFilters = () => {
    setSearch("");
    setCurrencyFilter("ALL");
    setDateFrom("");
    setDateTo("");
    setMinAmount("");
    setMaxAmount("");
    setPage(1);
    fetchTransactions(1, {
      search: "",
      currency: "ALL",
      dateFrom: "",
      dateTo: "",
      minAmount: "",
      maxAmount: "",
    });
  };

  const handleOpenDetails = async (txn) => {
    setSelectedTxn(txn);
    setDetailsDialog(true);
    setDetailData(null);
    setDetailLoading(true);
    try {
      const res = await getTransactionDetail(txn.id);
      setDetailData(res.data);
    } catch (e) {
      setDetailData({ transaction: txn, topupRequest: null });
    } finally {
      setDetailLoading(false);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <Box m={2}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          color="secondary"
          startIcon={<ArrowBack />}
          onClick={() => navigate("/customer-portal/wallet")}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography
          variant="h4"
          sx={{
            display: "flex",
            alignItems: "center",
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <History sx={{ mr: 1 }} />
          Transaction History
        </Typography>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<Tune />}
          onClick={() => setShowFilters((v) => !v)}
        >
          Filters
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                <InputBase
                  placeholder="Search by ID, type..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
                  sx={{ flexGrow: 1 }}
                />
                <IconButton size="small" onClick={handleApplyFilters}>
                  <Search />
                </IconButton>
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Currency</InputLabel>
                <Select
                  value={currencyFilter}
                  label="Currency"
                  onChange={(e) => setCurrencyFilter(e.target.value)}
                >
                  <MenuItem value="ALL">All</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="THB">THB</MenuItem>
                  <MenuItem value="LAK">LAK</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From"
                InputLabelProps={{ shrink: true }}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} md={1}>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                onClick={handleApplyFilters}
                startIcon={<Search />}
              >
                Apply
              </Button>
            </Grid>
            <Grid item xs={6} md={1}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                onClick={handleClearFilters}
                startIcon={<Clear />}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Box>
        {loading && rows.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="300px"
          >
            <CircularProgress />
            <Typography variant="h6" sx={{ ml: 2 }}>
              Loading transactions...
            </Typography>
          </Box>
        ) : rows.length === 0 ? (
          <Box textAlign="center" py={6}>
            <History sx={{ fontSize: 80, color: "text.disabled", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No transactions found
            </Typography>
          </Box>
        ) : (
          <>
            {isMobile ? (
              <Grid container spacing={2}>
                {rows.map((txn) => {
                  const cfg = getPurposeConfig(txn);
                  const sign = getAmountSign(txn);
                  return (
                    <Grid item xs={12} key={txn._id}>
                      <Card
                        sx={{
                          borderRadius: 2,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            mb={1.5}
                          >
                            <Box>
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={0.5}
                                mb={1}
                              >
                                <Typography
                                  variant="h6"
                                  fontWeight="bold"
                                  color={
                                    txn.txn_type === "credit"
                                      ? "success.main"
                                      : txn.txn_type === "debit"
                                        ? "error.dark"
                                        : "text.primary"
                                  }
                                >
                                  {sign}
                                  {formatCurrency(
                                    txn.amount || 0,
                                    txn.currency,
                                  )}
                                </Typography>
                                <CurrencyFlag currency={txn.currency} />
                              </Box>
                              <Chip
                                icon={cfg.icon}
                                label={cfg.label}
                                color={cfg.color}
                                size="small"
                              />
                            </Box>
                            <Button
                              color="secondary"
                              size="small"
                              variant="outlined"
                              startIcon={<Visibility />}
                              onClick={() => handleOpenDetails(txn)}
                            >
                              Details
                            </Button>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {txn.createdAt ? formatDate(txn.createdAt) : "—"}
                          </Typography>
                          {formatWalletTxnDetail(txn) && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}
                              noWrap
                            >
                              {formatWalletTxnDetail(txn)}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Transaction ID</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Balance After</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((txn) => {
                      const cfg = getPurposeConfig(txn);
                      const sign = getAmountSign(txn);
                      return (
                        <TableRow key={txn._id} hover>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.75rem",
                              }}
                              title={txn.id}
                            >
                              {txn.id || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={cfg.icon}
                              label={cfg.label}
                              color={cfg.color}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="flex-end"
                              gap={0.5}
                            >
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                color={
                                  txn.txn_type === "credit"
                                    ? "error.dark"
                                    : txn.txn_type === "debit"
                                      ? "success.main"
                                      : "text.primary"
                                }
                              >
                                {sign}
                                {formatCurrency(txn.amount || 0, txn.currency)}
                              </Typography>
                              <CurrencyFlag currency={txn.currency} />
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="flex-end"
                              gap={0.5}
                            >
                              <Typography variant="body2">
                                {formatCurrency(txn.balanceAfter, txn.currency)}
                              </Typography>
                              <CurrencyFlag currency={txn.currency} />
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {txn.createdAt ? formatDate(txn.createdAt) : "—"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 200 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              noWrap
                              title={formatWalletTxnDetail(txn)}
                            >
                              {formatWalletTxnDetail(txn) || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDetails(txn)}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {txn.purpose === "deposit" && (
                              <Tooltip title="View Evidence Photo">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedTxn(txn);
                                    setDetailsDialog(false);
                                    setImageDialog(true);
                                    handleOpenDetails(txn);
                                  }}
                                >
                                  <ImageIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" p={3}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, p) => setPage(p)}
                  color="secondary"
                />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Transaction Detail Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Transaction Details</DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : detailData ? (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              {/* Transaction Info */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1.5 }}>
                      Transaction
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={1}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Transaction ID
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {detailData.transaction.id}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Type
                        </Typography>
                        <Box>
                          {(() => {
                            const cfg = getPurposeConfig(
                              detailData.transaction,
                            );
                            return (
                              <Chip
                                icon={cfg.icon}
                                label={cfg.label}
                                color={cfg.color}
                                size="small"
                              />
                            );
                          })()}
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Amount
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="body1" fontWeight="bold">
                            {getAmountSign(detailData.transaction)}
                            {formatCurrency(
                              detailData.transaction.amount,
                              detailData.transaction.currency,
                            )}
                          </Typography>
                          <CurrencyFlag
                            currency={detailData.transaction.currency}
                          />
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Balance Before → After
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="body2">
                            {formatCurrency(
                              detailData.transaction.balanceBefore,
                              detailData.transaction.currency,
                            )}
                            {" → "}
                            {formatCurrency(
                              detailData.transaction.balanceAfter,
                              detailData.transaction.currency,
                            )}
                          </Typography>
                          <CurrencyFlag
                            currency={detailData.transaction.currency}
                          />
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Date
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(detailData.transaction.createdAt)}
                        </Typography>
                      </Box>
                      {formatWalletTxnDetail(detailData.transaction) && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Details
                          </Typography>
                          <Typography variant="body2">
                            {formatWalletTxnDetail(detailData.transaction)}
                          </Typography>
                        </Box>
                      )}
                      {detailData.transaction.remark && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Remark
                          </Typography>
                          <Typography variant="body2">
                            {detailData.transaction.remark}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Top-up Request Info (deposit only) */}
              <Grid item xs={12} md={6}>
                {detailData.topupRequest ? (
                  <Card
                    variant="outlined"
                    sx={{
                      borderColor:
                        detailData.topupRequest.status === "approved"
                          ? "success.main"
                          : "divider",
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 1.5 }}>
                        Top-up Request
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Status
                          </Typography>
                          <Box>
                            <Chip
                              label={
                                detailData.topupRequest.status
                                  .charAt(0)
                                  .toUpperCase() +
                                detailData.topupRequest.status.slice(1)
                              }
                              color={
                                detailData.topupRequest.status === "approved"
                                  ? "success"
                                  : "default"
                              }
                              size="small"
                            />
                          </Box>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Submitted
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(detailData.topupRequest.createdAt)}
                          </Typography>
                        </Box>
                        {detailData.topupRequest.reviewedBy && (
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Approved by
                            </Typography>
                            <Typography variant="body2">
                              {detailData.topupRequest.reviewedBy.name}
                            </Typography>
                          </Box>
                        )}
                        {detailData.topupRequest.approvalNotes && (
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Notes
                            </Typography>
                            <Typography variant="body2">
                              {detailData.topupRequest.approvalNotes}
                            </Typography>
                          </Box>
                        )}
                        {detailData.topupRequest.description && (
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Customer Note
                            </Typography>
                            <Typography variant="body2">
                              {detailData.topupRequest.description}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ) : detailData.transaction.purpose === "deposit" ? (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        No linked top-up request found.
                      </Typography>
                    </CardContent>
                  </Card>
                ) : null}
              </Grid>

              {/* Evidence Photo */}
              {detailData.topupRequest?.evidencePhoto && (
                <Grid item xs={12}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 1.5 }}>
                    Evidence Photo
                  </Typography>
                  <Card variant="outlined">
                    <CardContent>
                      <AuthenticatedImage
                        src={getEvidencePhotoUrl(
                          detailData.topupRequest.evidencePhoto.filename,
                        )}
                        alt="Evidence"
                        style={{
                          width: "100%",
                          height: "auto",
                          maxHeight: 320,
                          objectFit: "contain",
                          borderRadius: 4,
                        }}
                      />
                      <Box display="flex" justifyContent="flex-end" mt={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          startIcon={<ImageIcon />}
                          onClick={() => {
                            setDetailsDialog(false);
                            setImageDialog(true);
                          }}
                        >
                          View Full Size
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={() => setDetailsDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Full Size Evidence Image Dialog */}
      <Dialog
        open={imageDialog}
        onClose={() => setImageDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Evidence Photo</DialogTitle>
        <DialogContent>
          {detailData?.topupRequest?.evidencePhoto && (
            <AuthenticatedImage
              src={getEvidencePhotoUrl(
                detailData.topupRequest.evidencePhoto.filename,
              )}
              alt="Evidence"
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "70vh",
                objectFit: "contain",
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={() => setImageDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransactionHistory;
